<!--
  A conversation comment as the conversation shows it: who, when and on
  which snapshot, and its text; one written for a line outside the diff
  (see logic/offdiff) names the line, with links to it on GitHub and in
  the Files view. The inside of its card, in the Overview's conversation
  and the Review view's alike.
-->
<script lang="ts">
  import { subject, type PrStore } from '../state/pr.svelte';
  import type { CommentItem } from '../logic/reviewConversation';
  import { blobUrl, lineLabel, parseOffDiff } from '../logic/offdiff';
  import { parseDescNote } from '../logic/descNote';
  import { carryFilter } from '../logic/filefilter';
  import { relTime } from '../logic/time';
  import { nav } from '../nav.svelte';
  import { prPath } from '../router';
  import { session } from '../state/session.svelte';
  import Html from './Html.svelte';
  import PendingTag from './PendingTag.svelte';
  import SnapshotChip from './SnapshotChip.svelte';

  interface Props {
    pr: PrStore;
    comment: CommentItem;
    /** Show the passage of the description a comment on it quotes (the Overview); without it, the quote is only shown. */
    onpassage?: (id: string) => void;
  }
  let { pr, comment, onpassage }: Props = $props();

  const anchored = $derived(parseOffDiff(comment.body));
  /** A comment on a passage of the description (logic/descNote). */
  const onDescription = $derived(anchored ? null : parseDescNote(comment.body));
  const saving = $derived(pr.writes.on(subject.comment(comment.id)));
  /** The Files view at the anchored comment's line. */
  const codeHref = $derived.by(() => {
    if (!anchored) return null;
    const q = new URLSearchParams({ file: anchored.marker.path });
    if (anchored.marker.side === 'RIGHT') q.set('line', String(anchored.marker.line));
    return carryFilter(prPath(pr.owner, pr.repo, pr.number, 'files') + '?' + q, nav.query);
  });
</script>

<div class="comment-head">
  <b>{comment.author?.login ?? 'ghost'}</b><span title={comment.createdAt}>commented {relTime(comment.createdAt)}</span>
  {#if saving}<PendingTag label={saving.label} />{/if}
  <SnapshotChip
    {pr}
    commit={anchored?.marker.commit}
    at={comment.createdAt}
    path={anchored?.marker.path}
    line={anchored?.marker.side === 'RIGHT' ? anchored.marker.line : null}
    outdated={anchored ? { anchored: true } : null}
  />
  {#if anchored}
    <a
      class="anchor-chip"
      href={blobUrl(session.config?.github_web_url ?? 'https://github.com', pr.owner, pr.repo, anchored.marker)}
      target="_blank"
      rel="noreferrer"
      title="This line on GitHub at {anchored.marker.commit.slice(0, 7)}">{anchored.marker.path} · {lineLabel(anchored.marker)} ↗</a
    >
    <a href={codeHref}>view in code</a>
  {/if}
</div>
{#if onDescription}
  {@const q = onDescription.marker.quote}
  <blockquote class="desc-quote">
    <span class="muted small">On the description</span>
    {#if onpassage}
      <button type="button" class="desc-quote-go" onclick={() => onpassage(comment.id)} title="Show this passage in the description">{q.length > 240 ? q.slice(0, 240) + '…' : q}</button>
    {:else}
      <span>{q.length > 240 ? q.slice(0, 240) + '…' : q}</span>
    {/if}
  </blockquote>
  <Html md={onDescription.text} />
{:else if anchored}<Html md={anchored.text} />{:else}<Html html={comment.bodyHTML} md={comment.body} />{/if}

<style>
  .desc-quote {
    display: grid;
    gap: 2px;
    margin: 6px 0 8px;
    padding: 2px 10px;
    border-left: 3px solid var(--accent);
    font-size: 13px;
  }
  .desc-quote-go {
    all: unset;
    cursor: pointer;
    color: var(--fg);
  }
  .desc-quote-go:hover {
    text-decoration: underline;
  }
  .desc-quote-go:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
</style>
